import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';

/**
 * Service that patches network requests (fetch and XMLHttpRequest) to redirect
 * S3 requests to local proxy.
 * This is necessary because micro-frontends may use Axios or native fetch/XHR,
 * which bypass Angular's HTTP interceptors.
 * 
 * This patch is ONLY active in development mode when s3Proxy.enabled is true.
 */
@Injectable({ providedIn: 'root' })
export class S3ProxyPatchService {
  private isPatched = false;

  /**
   * Applies the XMLHttpRequest patch. Should be called once during app initialization.
   * Only applies the patch if enabled in environment configuration.
   */
  public applyPatch(): void {
    // Skip if already patched or disabled in environment
    if (this.isPatched || !environment.s3Proxy.enabled) {
      return;
    }

    const s3UrlPrefix = environment.s3Proxy.s3Url;
    const proxyUrlPrefix = environment.s3Proxy.proxyUrl;

    // Validate configuration
    if (!s3UrlPrefix || !proxyUrlPrefix) {
      console.warn('S3 proxy is enabled but URLs are not configured properly');
      return;
    }

    const originalOpen = XMLHttpRequest.prototype.open;

    XMLHttpRequest.prototype.open = function(method: string, url: string | URL, ...rest: any[]) {
      const urlString = url.toString();

      // Check if the URL is an S3 request
      if (urlString.startsWith(s3UrlPrefix)) {
        const proxiedUrl = urlString.replace(s3UrlPrefix, proxyUrlPrefix);
        
        // Call the original open with the proxied URL
        return originalOpen.call(this, method, proxiedUrl, ...rest);
      }

      // If not an S3 request, pass through unchanged
      return originalOpen.call(this, method, url, ...rest);
    };

    const originalFetch = window.fetch;
    window.fetch = function(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
      let urlString: string;

      // Extract URL from input (can be string, URL, or Request object)
      if (typeof input === 'string') {
        urlString = input;
      } else if (input instanceof URL) {
        urlString = input.toString();
      } else if (input instanceof Request) {
        urlString = input.url;
      } else {
        return;
      }

      // Check if the URL is an S3 request
      if (urlString.startsWith(s3UrlPrefix)) {
        const proxiedUrl = urlString.replace(s3UrlPrefix, proxyUrlPrefix);
        
        // Call the original fetch with the proxied URL
        return originalFetch.call(this, proxiedUrl, init);
      }

      // If not an S3 request, pass through unchanged
      return originalFetch.call(this, input, init);
    };

    this.isPatched = true;
  }
}

